import React, { useLayoutEffect, useRef } from 'react';
import { Box } from '@mui/material';
import ChatMessage from './ChatMessage';
import { Message } from './types';

interface MessagesListProps {
  messages: Message[];
  onApproval: (messageId: string, approved: boolean) => void;
  isLoading?: boolean;
  approvingMessageId?: string | null;
}

function findLastUserMessageId(messages: Message[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].isUser) return messages[i].id;
  }
  return null;
}

/** Ensures enough scroll range (bottom padding) then returns scrollTop to align anchor to the container top. */
function resolvePaddingAndGetTargetScrollTop(
  container: HTMLElement,
  inner: HTMLElement,
  anchor: HTMLElement
): number {
  inner.style.paddingBottom = '0px';

  for (let attempt = 0; attempt < 10; attempt++) {
    const cRect = container.getBoundingClientRect();
    const aRect = anchor.getBoundingClientRect();
    const scrollTopNeeded = container.scrollTop + (aRect.top - cRect.top);
    const maxScroll = Math.max(
      0,
      container.scrollHeight - container.clientHeight
    );

    if (scrollTopNeeded <= maxScroll + 0.5) {
      return Math.max(0, scrollTopNeeded);
    }

    const extra = Math.ceil(scrollTopNeeded - maxScroll) + 2;
    const current = parseFloat(inner.style.paddingBottom) || 0;
    inner.style.paddingBottom = `${current + extra}px`;
  }

  const cRect = container.getBoundingClientRect();
  const aRect = anchor.getBoundingClientRect();
  const scrollTopNeeded = container.scrollTop + (aRect.top - cRect.top);
  const maxScroll = Math.max(
    0,
    container.scrollHeight - container.clientHeight
  );
  return Math.min(maxScroll, Math.max(0, scrollTopNeeded));
}

const MessagesList: React.FC<MessagesListProps> = ({
  messages,
  onApproval,
  isLoading = false,
  approvingMessageId
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const innerContentRef = useRef<HTMLDivElement>(null);

  const pinMessageId = findLastUserMessageId(messages);

  useLayoutEffect(() => {
    if (!pinMessageId) return;

    const container = scrollContainerRef.current;
    const inner = innerContentRef.current;
    if (!container || !inner) return;

    const anchor = container.querySelector<HTMLElement>(
      `#ai-assistant-msg-${CSS.escape(pinMessageId)}`
    );
    if (!anchor) return;

    let cancelled = false;
    const outerRaf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        const target = resolvePaddingAndGetTargetScrollTop(
          container,
          inner,
          anchor
        );
        container.scrollTo({ top: target, behavior: 'smooth' });
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(outerRaf);
    };
  }, [pinMessageId]);

  return (
    <Box
      ref={scrollContainerRef}
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }}
    >
      <Box
        ref={innerContentRef}
        sx={{ display: 'flex', flexDirection: 'column' }}
      >
        {messages.map((message) => (
          <Box key={message.id} id={`ai-assistant-msg-${message.id}`}>
            <ChatMessage
              message={message}
              onApproval={onApproval}
              isLoading={isLoading}
              approvingMessageId={approvingMessageId}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default MessagesList;
